import { useState } from 'react'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { InterestSelector } from '../../components/inputs/interest-selector'
import { getInterestErrorMessage } from '../../redux-toolkits/interests/interests.helpers'
import {
  useGetSpendingQuestionsQuery,
  useUpdateSpendingQuestionMutation,
  type SpendingQuestion,
} from '../../redux-toolkits/spending-questions/spending-question-slice'
import { SpendingQuestionForm } from './spending-question-form'

/** The Spending Questions tab of the Shopping Preferences page. */
export function SpendingQuestionsPanel() {
  const {
    data: questions = [],
    isLoading,
    isError,
    refetch,
  } = useGetSpendingQuestionsQuery()
  const [selected, setSelected] = useState<SpendingQuestion | 'new' | null>(
    null,
  )
  const [updateQuestion, { isLoading: isUpdating }] =
    useUpdateSpendingQuestionMutation()

  const toggleActive = async (question: SpendingQuestion) => {
    try {
      await updateQuestion({
        id: question._id,
        body: { isActive: !question.isActive },
      }).unwrap()
      toast.success(
        question.isActive ? 'Question deactivated' : 'Question activated',
      )
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-pry-gray">
          Manage the questions and answer options customers see during
          onboarding.
        </p>
        {selected === null && (
          <Button
            label="Create question"
            primary
            size="medium"
            onClick={() => setSelected('new')}
          />
        )}
      </div>
      {selected !== null && (
        <SpendingQuestionForm
          key={selected === 'new' ? 'new' : selected._id}
          question={selected === 'new' ? undefined : selected}
          onClose={() => setSelected(null)}
        />
      )}
      {isLoading && <p>Loading questions…</p>}
      {isError && (
        <div role="alert">
          <p className="text-danger">Unable to load spending questions.</p>
          <button
            type="button"
            className="mt-2 underline"
            onClick={() => {
              void refetch()
            }}
          >
            Try again
          </button>
        </div>
      )}
      {!isLoading && !isError && questions.length === 0 && (
        <p className="rounded-2xl border border-divider-gray p-8 text-center">
          No spending questions yet. Create your first question above.
        </p>
      )}
      {questions.map((question) => (
        <article
          key={question._id}
          className="space-y-3 rounded-2xl border border-divider-gray bg-white p-6"
        >
          <div className="flex flex-wrap justify-between gap-3">
            <h2 className="text-lg font-semibold">{question.title}</h2>
            <span className="text-sm text-pry-gray">
              {question.isActive ? 'Active' : 'Inactive'} · Order{' '}
              {question.displayOrder}
            </span>
          </div>
          <p className="text-pry-gray">{question.description}</p>
          <ol className="list-inside list-decimal space-y-3">
            {question.options.map((option, index) => (
              <li key={option._id ?? index}>
                {option.label}
                <div className="mt-2 pl-5">
                  <InterestSelector selectedIds={option.interestIds ?? []} />
                </div>
              </li>
            ))}
          </ol>
          <div className="flex gap-4 pt-2">
            <button
              type="button"
              disabled={selected !== null}
              className="font-medium text-primary disabled:opacity-40"
              onClick={() => setSelected(question)}
            >
              Edit
            </button>
            <button
              type="button"
              disabled={isUpdating || selected !== null}
              className="font-medium text-primary disabled:opacity-40"
              onClick={() => {
                void toggleActive(question)
              }}
            >
              {question.isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </article>
      ))}
    </section>
  )
}
